import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { getWorkflowStatus } from "@/lib/workflowStatus";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

type ActivityLog = {
  id: string;
  tenant_id: string;
  user_id: string | null;
  action: string;
  entity: string;
  entity_id: string;
  entity_name: string | null;
  created_at: string;
};

interface Props {
  job: Job;
  tenantId: string;
}

const ACTION_CFG: Record<string, { icon: string; color: string; label: string }> = {
  created:   { icon: "ri-add-circle-line",      color: "bg-emerald-100 text-emerald-600",  label: "Created" },
  updated:   { icon: "ri-edit-circle-line",     color: "bg-amber-100 text-amber-600",      label: "Updated" },
  deleted:   { icon: "ri-delete-bin-line",      color: "bg-red-100 text-red-500",          label: "Deleted" },
  sent:      { icon: "ri-send-plane-line",      color: "bg-sky-100 text-sky-600",          label: "Sent" },
  delivered: { icon: "ri-check-double-line",    color: "bg-emerald-100 text-emerald-600",  label: "Delivered" },
  signed:    { icon: "ri-pen-nib-line",         color: "bg-violet-100 text-violet-600",    label: "Signed" },
  paid:      { icon: "ri-money-dollar-circle-line", color: "bg-[#C8A96E]/20 text-[#a6853e]", label: "Paid" },
  viewed:    { icon: "ri-eye-line",             color: "bg-slate-100 text-slate-500",      label: "Viewed" },
};

const ENTITY_LABEL: Record<string, string> = {
  job:         "Job",
  payment:     "Payment",
  contract:    "Contract",
  delivery:    "Delivery",
  photo:       "Photo",
  step:        "Workflow Step",
  note:        "Note",
  message:     "Message",
};

function getCfg(action: string) {
  return ACTION_CFG[action] ?? { icon: "ri-radio-button-line", color: "bg-slate-100 text-slate-400", label: action };
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDateTime(d);
}

export default function ActivityTab({ job, tenantId }: Props) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const wfCfg = getWorkflowStatus(job.workflow_status || job.status);

  const fetchLogs = useCallback(async () => {
    const { data } = await supabase
      .from("activity_logs")
      .select("*")
      .eq("tenant_id", tenantId)
      .eq("entity_id", job.id)
      .order("created_at", { ascending: false })
      .limit(60);
    setLogs((data ?? []) as ActivityLog[]);
    setLoading(false);
  }, [tenantId, job.id]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Add creation entry if not in logs
  const creationEntry: ActivityLog = {
    id: "job-creation",
    tenant_id: tenantId,
    user_id: null,
    action: "created",
    entity: "job",
    entity_id: job.id,
    entity_name: job.title,
    created_at: job.created_at,
  };

  const timeline = [
    ...logs,
    ...(logs.some((l) => l.entity === "job" && l.action === "created") ? [] : [creationEntry]),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Quick stats
  const statusUpdates = timeline.filter((l) => l.action === "updated" && l.entity === "job").length;
  const paymentEvents = timeline.filter((l) => l.entity === "payment").length;
  const deliveryEvents = timeline.filter((l) => l.entity === "delivery" || l.action === "delivered").length;

  return (
    <div className="space-y-5">
      {/* Job summary card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-box-line text-[#C8A96E]"></i>
          Job Summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${wfCfg.bg} ${wfCfg.color} border ${wfCfg.border} mb-1`}>
              <i className={`${wfCfg.icon} text-[11px]`}></i>
              {wfCfg.label}
            </div>
            <p className="text-slate-400 text-xs">Status</p>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-bold text-lg">{statusUpdates}</p>
            <p className="text-slate-400 text-xs">Status Changes</p>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-bold text-lg">{paymentEvents}</p>
            <p className="text-slate-400 text-xs">Payment Events</p>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-bold text-lg">{deliveryEvents}</p>
            <p className="text-slate-400 text-xs">Deliveries</p>
          </div>
        </div>

        {/* Key dates */}
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
          {[
            { label: "Created", value: job.created_at, icon: "ri-add-circle-line", color: "text-emerald-500" },
            { label: "Shoot Date", value: job.shoot_date, icon: "ri-camera-3-line", color: "text-sky-500" },
            { label: "Editing Started", value: job.editing_started_at, icon: "ri-edit-2-line", color: "text-violet-500" },
            { label: "Delivered", value: job.delivered_at, icon: "ri-send-plane-fill", color: "text-emerald-500" },
            { label: "Completed", value: job.completed_at, icon: "ri-checkbox-circle-fill", color: "text-emerald-600" },
          ].filter((d) => d.value).map((d) => (
            <div key={d.label} className="flex items-center gap-2">
              <i className={`${d.icon} text-xs ${d.color} w-3 flex-shrink-0`}></i>
              <div>
                <p className="text-slate-400 text-[10px]">{d.label}</p>
                <p className="text-slate-600 text-xs font-medium">{fmtDateTime(d.value!)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
            <i className="ri-history-line text-[#C8A96E]"></i>
            Activity Timeline
          </h3>
          <span className="text-slate-300 text-xs">{timeline.length} events</span>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : timeline.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
              <i className="ri-time-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm font-medium">No activity recorded yet</p>
            <p className="text-slate-300 text-xs max-w-xs">Status changes, deliveries and payment events will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {timeline.map((log, idx) => {
              const cfg = getCfg(log.action);
              const entityLabel = ENTITY_LABEL[log.entity] ?? log.entity;
              const isFirst = idx === 0;
              return (
                <div
                  key={log.id ?? idx}
                  className={`flex items-start gap-3 px-5 py-3.5 transition-colors hover:bg-slate-50/50 ${isFirst ? "bg-[#C8A96E]/3" : ""}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${cfg.color}`}>
                    <i className={`${cfg.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-slate-700 text-sm">
                          <span className="font-medium">{cfg.label}</span>
                          {" "}
                          <span className="text-slate-500">{entityLabel}</span>
                          {log.entity_name && log.entity_name !== job.title && (
                            <span className="text-slate-400 text-xs"> — &ldquo;{log.entity_name}&rdquo;</span>
                          )}
                        </p>
                        <p className="text-slate-300 text-[10px] mt-0.5 flex items-center gap-1">
                          <i className="ri-time-line text-[10px]"></i>
                          {fmtDateTime(log.created_at)}
                        </p>
                      </div>
                      <span className="text-slate-300 text-[10px] whitespace-nowrap shrink-0 mt-0.5">
                        {timeAgo(log.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}